import React from 'react';
import {
  Box,
  IconButton,
  LinearProgress,
  Stack,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { FormattedMessage, useIntl } from 'react-intl';

const shellStyles = {
  bgcolor: 'background.default',
  display: 'flex',
  flexDirection: 'column',
  height: '100dvh',
  minHeight: 0,
  width: '100%',
};
const barStyles = {
  bgcolor: 'background.paper',
  flexGrow: 0,
  flexShrink: 0,
  px: { xs: 2, md: 4 },
  py: { xs: 1.5, md: 2 },
};
const topBarStyles = {
  ...barStyles,
  borderBottom: '1px solid',
  borderColor: 'divider',
};
const bottomBarStyles = {
  ...barStyles,
  borderTop: '1px solid',
  borderColor: 'divider',
};
const mainStyles = {
  display: 'flex',
  flexDirection: 'column',
  flexGrow: 1,
  minHeight: 0,
  overflow: 'auto',
  p: { xs: 2, md: 4 },
};
const progressStyles = { width: { xs: '100%', sm: 240 } };
const headerTextStyles = { flexGrow: 1 };
const headerControlStyles = {
  alignItems: 'flex-end',
  alignSelf: { xs: 'stretch', sm: 'flex-end' },
  flexShrink: 0,
};
const headerAlignment = { xs: 'flex-start', sm: 'center' } as const;
const headerDirection = { xs: 'column', sm: 'row' } as const;
const footerDirection = { xs: 'column-reverse', sm: 'row' } as const;

interface ImportWorkflowShellProps {
  step: string;
  description: string;
  closeDisabled?: boolean;
  progressLabel?: string;
  progressValue?: number;
  stepIcon?: React.ReactNode;
  headerControl?: React.ReactNode;
  onClose: () => void;
  actions: React.ReactNode;
  children: React.ReactNode;
}

export const ImportWorkflowShell = React.memo(
  ({
    step,
    description,
    closeDisabled = false,
    progressLabel,
    progressValue,
    stepIcon,
    headerControl,
    onClose,
    actions,
    children,
  }: ImportWorkflowShellProps) => {
    const intl = useIntl();
    const progressVariant =
      progressValue == null ? 'indeterminate' : 'determinate';

    return (
      <Box sx={shellStyles}>
        <Box component="header" sx={topBarStyles}>
          <Stack gap={1.5}>
            <Stack
              alignItems="center"
              direction="row"
              justifyContent="space-between"
            >
              <Typography variant="h5">
                <FormattedMessage
                  id="imports.workflow.title"
                  defaultMessage="Import finds"
                />
              </Typography>
              <IconButton
                aria-label={intl.formatMessage({
                  id: 'imports.workflow.close',
                  defaultMessage: 'Close import',
                })}
                disabled={closeDisabled}
                onClick={onClose}
              >
                <CloseIcon />
              </IconButton>
            </Stack>
            <Stack
              alignItems={headerAlignment}
              direction={headerDirection}
              gap={2}
              justifyContent="space-between"
            >
              <Box sx={headerTextStyles}>
                <Stack alignItems="center" direction="row" gap={0.75}>
                  {stepIcon}
                  <Typography fontWeight={600}>{step}</Typography>
                </Stack>
                <Typography color="text.secondary" variant="body2">
                  {description}
                </Typography>
              </Box>
              {(progressLabel || headerControl) && (
                <Stack direction="row" gap={2} sx={headerControlStyles}>
                  {progressLabel && (
                    <Stack sx={progressStyles} gap={0.5}>
                      <Typography variant="body2">{progressLabel}</Typography>
                      <LinearProgress
                        value={progressValue}
                        variant={progressVariant}
                      />
                    </Stack>
                  )}
                  {headerControl}
                </Stack>
              )}
            </Stack>
          </Stack>
        </Box>

        <Box component="main" sx={mainStyles}>
          {children}
        </Box>

        <Box component="footer" sx={bottomBarStyles}>
          <Stack direction={footerDirection} gap={1} justifyContent="flex-end">
            {actions}
          </Stack>
        </Box>
      </Box>
    );
  }
);
